import type { BrandPreviewId } from './shared';

/**
 * Hyrox brand skin: Forge token overrides.
 *
 * Dark (dashboard dark mode + the hub, which is pinned dark): Hyrox yellow
 * accent on near-black surfaces, with black text on yellow fills via
 * --fv-accent-text.
 *
 * Light (dashboard only): yellow text on white is unreadable, so the accent
 * becomes Hyrox black (buttons, active nav, links) with the yellow kept as a
 * wash (--fv-accent-dim) behind active states and as the text colour on black
 * fills (--fv-accent-text). That is the classic black-and-yellow Hyrox
 * lockup without ever putting yellow text on a white surface.
 *
 * The rule the whole skin keeps, in both token sets (old --fv-* and v8 --fvd-*):
 *   - a yellow FILL always carries dark ink (#111111);
 *   - yellow is TEXT only on black or near-black grounds, never on a light one;
 *   - on light grounds the accent is black, and yellow appears only as the
 *     label on a black fill and as a wash behind dark text.
 * brand-preview.test.ts measures every pairing against WCAG AA.
 */

export const HYROX_YELLOW = '#FFED00';
/** The ink on every yellow fill. */
export const HYROX_INK = '#111111';

const HYROX_DARK_VARS: Record<string, string> = {
  '--fv-bg': '#0B0B0B',
  '--fv-bg-raised': '#161616',
  '--fv-bg-card': '#1C1C1C',
  '--fv-bg-input': '#262626',
  '--fv-text-primary': '#FFFFFF',
  '--fv-text-secondary': '#C9C9C9',
  '--fv-text-tertiary': '#8F8F8F',
  '--fv-text-inverse': '#0B0B0B',
  '--fv-border': 'rgba(255,255,255,0.10)',
  '--fv-border-strong': 'rgba(255,255,255,0.16)',
  '--fv-accent': HYROX_YELLOW,
  '--fv-accent-hover': '#E8D800',
  '--fv-accent-dim': 'rgba(255,237,0,0.10)',
  '--fv-accent-border': 'rgba(255,237,0,0.35)',
  '--fv-accent-text': HYROX_INK,
  '--fv-hover': 'rgba(255,255,255,0.05)',
  '--fv-hover-strong': 'rgba(255,255,255,0.08)',
  '--chart-1': HYROX_YELLOW,
  '--fv-chart-bar': HYROX_YELLOW,
  '--fv-chart-bar-deep': '#B8A600',
  '--fv-chart-value': HYROX_YELLOW,
  '--fv-chart-value-hover': '#FFF56B',
  '--fv-chart-value-weight': '500',
};

const HYROX_LIGHT_VARS: Record<string, string> = {
  '--fv-bg': '#F4F4F3',
  '--fv-bg-raised': '#FFFFFF',
  '--fv-bg-card': '#FFFFFF',
  '--fv-bg-input': '#FFFFFF',
  '--fv-text-primary': '#111111',
  '--fv-text-secondary': '#565656',
  '--fv-text-tertiary': '#9A9A9A',
  '--fv-text-inverse': '#FFFFFF',
  '--fv-border': 'rgba(0,0,0,0.09)',
  '--fv-border-strong': 'rgba(0,0,0,0.16)',
  '--fv-accent': '#111111',
  '--fv-accent-hover': '#2E2E2E',
  '--fv-accent-dim': 'rgba(255,237,0,0.28)',
  '--fv-accent-border': 'rgba(17,17,17,0.30)',
  '--fv-accent-text': HYROX_YELLOW,
  '--fv-hover': 'rgba(0,0,0,0.04)',
  '--fv-hover-strong': 'rgba(0,0,0,0.07)',
  '--chart-1': '#111111',
  '--fv-chart-bar': '#111111',
  '--fv-chart-bar-deep': '#3A3A3A',
  // Yellow figures are unreadable on white; black keeps the light chart legible
  '--fv-chart-value': '#111111',
  '--fv-chart-value-hover': '#2E2E2E',
  '--fv-chart-value-weight': '500',
};

/**
 * The same skin in the v8 tokens (--fvd-*), which every Dashboard v8 screen
 * and the reskinned athlete hub read instead of --fv-*. Without these the v8
 * surfaces came out Fitvent teal inside a HYROX demo.
 *
 * Every colour token the v8 palette defines is restated, not just the accent,
 * because the hub pins this dark set even while next-themes has `.light` on
 * <html>: anything left out would fall through to the LIGHT :root value and
 * land on a black page (light shadows, light tag colours, the light logo ink).
 *
 * "Needs you" amber is pushed towards orange in dark: the stock amber sits too
 * close to HYROX yellow, and yellow here means done or selected.
 */
export const HYROX_FVD_DARK: Record<string, string> = {
  '--fvd-canvas': '#0B0B0B',
  '--fvd-elevated': '#1A1A1A',
  '--fvd-elevated-hairline': 'rgba(255,255,255,0.14)',
  '--fvd-rail': '#111111',
  '--fvd-row-hover': '#151515',
  '--fvd-neutral-chip': '#1C1C1C',
  '--fvd-selected-fill': 'rgba(255,255,255,0.08)',
  // Chosen: yellow, as everything selected is under this skin.
  '--fvd-selected-border': HYROX_YELLOW,
  '--fvd-selected-on': HYROX_INK,
  '--fvd-ink': '#FFFFFF',
  '--fvd-ink-secondary': '#C9C9C9',
  '--fvd-ink-muted': '#A3A3A3',
  '--fvd-ink-disabled': '#6B6B6B',
  '--fvd-hairline-strong': 'rgba(255,255,255,0.16)',
  '--fvd-hairline': 'rgba(255,255,255,0.10)',
  '--fvd-hairline-light': 'rgba(255,255,255,0.07)',
  '--fvd-border-control': 'rgba(255,255,255,0.18)',
  '--fvd-hover-tint': 'rgba(255,255,255,0.05)',
  '--fvd-teal': HYROX_YELLOW,
  '--fvd-teal-hover': '#E8D800',
  '--fvd-teal-on': HYROX_INK,
  '--fvd-switch-off': 'rgba(255,255,255,0.24)',
  '--fvd-switch-thumb': '#FFFFFF',
  '--fvd-switch-thumb-shadow': '0 1px 2px rgba(0,0,0,0.45)',
  '--fvd-teal-text': HYROX_YELLOW,
  '--fvd-teal-tint': 'rgba(255,237,0,0.12)',
  '--fvd-teal-tint-pale': 'rgba(255,237,0,0.06)',
  '--fvd-teal-solid': HYROX_YELLOW,
  '--fvd-teal-solid-on': HYROX_INK,
  '--fvd-amber': '#F2994A',
  '--fvd-amber-text': '#F5A962',
  '--fvd-amber-text-dark': '#F8C08E',
  '--fvd-amber-tint': 'rgba(242,153,74,0.15)',
  '--fvd-amber-tint-pale': 'rgba(242,153,74,0.07)',
  '--fvd-danger': '#E4674A',
  '--fvd-danger-tint': 'rgba(228,103,74,0.14)',
  '--fvd-danger-on': '#1A0906',
  '--fvd-info': '#8AA6E0',
  '--fvd-info-tint': 'rgba(138,166,224,0.14)',
  '--fvd-tag-men-bg': 'rgba(138,166,224,0.14)',
  '--fvd-tag-men-fg': '#A9BFEA',
  '--fvd-tag-women-bg': 'rgba(224,138,176,0.14)',
  '--fvd-tag-women-fg': '#E8A9C4',
  '--fvd-tag-mixed-bg': 'rgba(170,150,230,0.15)',
  '--fvd-tag-mixed-fg': '#C1B2EE',
  '--fvd-chart-1': '#262626',
  '--fvd-chart-2': '#383838',
  '--fvd-chart-3': '#575757',
  '--fvd-chart-4': '#B3A600',
  '--fvd-chart-5': HYROX_YELLOW,
  '--fvd-switcher-bg': '#1A1A1A',
  '--fvd-switcher-bg-hover': '#242424',
  '--fvd-logo-tile-bg': '#F2F2F2',
  '--fvd-logo-tile-ink': '#4E4E4E',
  '--fvd-logo-tile-bg-alt': '#0F0E0C',
  // The Fitvent mark's own dark-ground pair, for any lockup still drawn.
  '--fvd-brand-ink': '#F4FAF8',
  '--fvd-brand-accent': '#2ADFDF',
  '--fvd-mark-accent': '#2ADFDF',
  '--fvd-mark-ink': '#F4FAF8',
  '--fvd-shadow-dropdown': '0 12px 28px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.4)',
  '--fvd-shadow-modal': '0 0 0 1px rgba(255,255,255,0.10), 0 24px 60px rgba(0,0,0,0.72)',
  '--fvd-overlay': 'rgba(0,0,0,0.62)',
  '--fvd-shadow-tooltip': '0 6px 18px rgba(0,0,0,0.6)',
  '--fvd-font-body': "'Brutal Type', Arial, sans-serif",
};

/**
 * Light v8 (dashboard only). Black is the accent, so a primary button is a
 * black fill with a yellow label and selected rows get a yellow wash behind
 * black text. Amber, danger, info and the tags keep their stock light values,
 * which already pass on white and read as different from black.
 */
export const HYROX_FVD_LIGHT: Record<string, string> = {
  '--fvd-canvas': '#FFFFFF',
  '--fvd-elevated': '#FFFFFF',
  '--fvd-elevated-hairline': 'rgba(17,17,17,0.10)',
  '--fvd-rail': '#FAFAFA',
  '--fvd-row-hover': '#F7F7F7',
  '--fvd-neutral-chip': '#F2F2F2',
  '--fvd-selected-fill': '#EDEDED',
  // Chosen on light: the black line, with yellow on a filled option.
  '--fvd-selected-border': HYROX_INK,
  '--fvd-selected-on': HYROX_YELLOW,
  '--fvd-ink': HYROX_INK,
  '--fvd-ink-secondary': '#474747',
  '--fvd-ink-muted': '#5C5C5C',
  '--fvd-ink-disabled': '#9E9E9E',
  '--fvd-hairline-strong': 'rgba(17,17,17,0.14)',
  '--fvd-hairline': 'rgba(17,17,17,0.10)',
  '--fvd-hairline-light': 'rgba(17,17,17,0.07)',
  '--fvd-border-control': 'rgba(17,17,17,0.16)',
  '--fvd-hover-tint': 'rgba(17,17,17,0.045)',
  '--fvd-teal': HYROX_INK,
  '--fvd-teal-hover': '#333333',
  '--fvd-teal-on': HYROX_YELLOW,
  '--fvd-switch-off': '#D6D6D6',
  '--fvd-teal-text': HYROX_INK,
  '--fvd-teal-tint': 'rgba(255,237,0,0.32)',
  '--fvd-teal-tint-pale': 'rgba(255,237,0,0.12)',
  '--fvd-teal-solid': HYROX_INK,
  '--fvd-teal-solid-on': HYROX_YELLOW,
  '--fvd-chart-1': '#ECECEC',
  '--fvd-chart-2': '#D9D9D9',
  '--fvd-chart-3': '#B5B5B5',
  '--fvd-chart-4': '#6E6E6E',
  '--fvd-chart-5': HYROX_INK,
  '--fvd-switcher-bg': '#F4F4F4',
  '--fvd-switcher-bg-hover': '#EDEDED',
  '--fvd-font-body': "'Brutal Type', Arial, sans-serif",
};

/**
 * Inside a `.fvd-compat` subtree (old markup shown in a v8 screen) globals.css
 * maps --fv-accent onto --fvd-teal but leaves the text on it white, and pins
 * the shadcn label colour to near-white and the old faces to Geist. Under the
 * skin that put white labels on yellow, so the label follows the v8 one.
 */
function compatCss(root: string): string {
  return `${root} .fvd-compat { --fv-accent-text: var(--fvd-teal-on); --primary-foreground: var(--fvd-teal-on); --font-instrument-sans: 'Brutal Type', Arial, sans-serif; --fv-font-body: 'Brutal Type', Arial, sans-serif; --fv-font-display: 'Brutal Type', Arial, sans-serif; }`;
}

function toDeclarations(vars: Record<string, string>): string {
  return Object.entries(vars)
    .map(([k, v]) => `${k}: ${v};`)
    .join(' ');
}

/**
 * Brutal Type (Hyrox's brand typeface), self-hosted for the demo skin only.
 * Only Light and Medium files exist: regular body text (100-449) maps to
 * Light (light-on-dark reads heavier, and all-Medium body copy looked
 * uniformly bold), while 450-900 maps to Medium so 600/700 headings
 * resolve to a declared face and the browser synthesises the bold. Declaring the full range rather than a
 * single weight also avoids the iOS Safari fallback where bold headings
 * silently drop to the system font.
 *
 * The variable overrides are scoped to `body` because next/font defines
 * --font-barlow-condensed and --font-instrument-sans via a class ON body;
 * redefining them on <html> would lose that specificity fight. Overriding
 * these two vars reskins both the --fv-font-* chain and the many direct
 * var(--font-barlow-condensed) inline styles. Geist Mono is kept for
 * tabular stat figures.
 */
const HYROX_FONT_CSS = [
  `@font-face { font-family: 'Brutal Type'; src: url('/brand/hyrox/fonts/BrutalType-Light.ttf') format('truetype'); font-weight: 100 449; font-style: normal; font-display: swap; }`,
  `@font-face { font-family: 'Brutal Type'; src: url('/brand/hyrox/fonts/BrutalType-Medium.ttf') format('truetype'); font-weight: 450 900; font-style: normal; font-display: swap; }`,
  // font-family on body itself covers all text that inherits the Tailwind
  // preflight default stack (most hub/dashboard body copy has no explicit
  // font class); the var overrides cover everything that names a font.
  `html[data-brand-preview="hyrox"] body { font-family: 'Brutal Type', Arial, sans-serif; --font-barlow-condensed: 'Brutal Type', 'Arial Narrow', sans-serif; --font-instrument-sans: 'Brutal Type', Arial, sans-serif; --font-geist-sans: 'Brutal Type', Arial, sans-serif; }`,
].join('\n');

export type BrandPreviewSurface = 'dashboard' | 'hub';

/**
 * CSS injected by BrandPreviewSkin. Selectors target the data attribute set
 * on <html> so Radix portals (dialogs, sheets, dropdowns) inherit the skin.
 * The attribute selector out-specifies the plain `.light` override in
 * globals.css, so the `.light` variant here must restate everything it needs.
 */
export function brandPreviewCss(brand: BrandPreviewId, surface: BrandPreviewSurface): string {
  if (brand !== 'hyrox') return '';
  const root = `html[data-brand-preview="hyrox"]`;
  if (surface === 'hub') {
    // The hub is dark-only under the Hyrox skin: the same dark palette applies
    // even when next-themes has put `.light` on <html>.
    return [
      `${root}, ${root}.light, ${root}.dark { ${toDeclarations(HYROX_DARK_VARS)} ${toDeclarations(HYROX_FVD_DARK)} color-scheme: dark; }`,
      compatCss(root),
      HYROX_FONT_CSS,
    ].join('\n');
  }
  return [
    `${root} { ${toDeclarations(HYROX_DARK_VARS)} }`,
    // :not(.light) rather than the bare root, so no dark v8 value the light
    // set does not restate can leak into light mode.
    `${root}:not(.light) { ${toDeclarations(HYROX_FVD_DARK)} }`,
    `${root}.light { ${toDeclarations(HYROX_LIGHT_VARS)} ${toDeclarations(HYROX_FVD_LIGHT)} color-scheme: light; }`,
    compatCss(root),
    HYROX_FONT_CSS,
  ].join('\n');
}

/** The hub's v8 tokens alone, for HubShell (see components/hub/hub-skin.ts). */
export function hyroxHubFvdCss(): string {
  const root = `html[data-brand-preview="hyrox"]`;
  return `${root}, ${root}.light, ${root}.dark { ${toDeclarations(HYROX_FVD_DARK)} color-scheme: dark; }`;
}
